import React from 'react';
import { Switch, InputNumber, Tooltip } from 'antd';
import { IBookingLimitRule } from 'types/bookingLimit';
import s from './s.module.less';

export interface IDraft {
    enabled: boolean;
    limitCount: number | null;
}

/**
 * 这一行为什么不能编辑。原来是 readOnly / locked / lockedHelper 三个 prop，
 * 但它们都由调用方从同一个 listingState 推出来，且组合是互斥的。
 *  - edit          可编辑
 *  - readonly      团队成员：改成状态陈述（On/Off 胶囊 + 纯文本），不是禁用控件
 *  - notListed     还没上架：禁用控件 + tooltip 说明原因
 *  - practicePaused 整个 practice 被暂停：禁用控件，原因由页面上的提示条讲
 */
export type TLimitRowMode = 'edit' | 'readonly' | 'notListed' | 'practicePaused';

interface Props {
    /** 已保存的规则 —— 用量条画的是它，不是草稿 */
    saved?: IBookingLimitRule;
    draft: IDraft;
    onChange: (next: IDraft) => void;
    mode: TLimitRowMode;
    errMsg: string | null;
    /**
     * 已触顶。**必须由调用方传**：LimitRow 自己看不到 used/cap。
     * 漏传会让 .limitFull 那套琥珀色样式永远不生效（本来就漏了一次）。
     */
    full?: boolean;
    /** practice 视图的单位后缀「, per provider」——强调不是共享池（原型 :834） */
    perProvider?: boolean;
    /** 数字输入框的 ref。公告弹窗点「Set a limit」后要聚焦它（原型 focusCap(true)） */
    capRef?: React.Ref<HTMLInputElement>;
    /**
     * 用量区内容。solo 是「9 of 20 used」+ 进度条；practice 是「1 paused this week」等人数口径，
     * 两者差异大，交给调用方渲染。
     */
    usage?: React.ReactNode;
}

const NOT_LISTED_HINT = 'Available once your listing is live.';

const LimitRow: React.FC<Props> = ({
    saved,
    draft,
    onChange,
    mode,
    errMsg,
    full,
    perProvider,
    capRef,
    usage,
}) => {
    const savedOn = !!saved?.enabled;
    const readOnly = mode === 'readonly';
    const disabled = mode !== 'edit';

    /**
     * 用量条只在「草稿开着 且 已保存的也开着」时显示：
     *  - 草稿刚打开、还没保存：没有已生效的上限，没什么可对照的
     *  - 草稿刚关闭：还画着进度条会和开关状态自相矛盾
     */
    const showUsage = draft.enabled && savedOn;

    const cls = [
        s.limit,
        draft.enabled ? s.limitOn : s.limitOff,
        // 顺序有意：出错时红色压过琥珀色
        full && !errMsg ? s.limitFull : '',
        errMsg ? s.limitErr : '',
    ]
        .filter(Boolean)
        .join(' ');

    /**
     * 只读成员看到的不是禁用控件，而是状态陈述：一个 On/Off 胶囊 + 一行纯文本
     * （原型 limitRow 的 ro 分支）。禁用态的开关和输入框会读成「坏掉的控件」，
     * 而这里要传达的是「这个值是这样，且不由你改」。
     */
    const toggle = (
        <Switch
            className={s.toggle}
            checked={draft.enabled}
            disabled={disabled}
            onChange={(checked) =>
                onChange({
                    enabled: checked,
                    // 关掉不清空数字：后端也保留，再次打开时直接沿用
                    limitCount: draft.limitCount,
                })
            }
        />
    );

    let firstCell: React.ReactNode = toggle;
    if (mode === 'notListed') {
        firstCell = <Tooltip title={NOT_LISTED_HINT}>{toggle}</Tooltip>;
    } else if (readOnly) {
        firstCell = (
            <span className={`${s.pill} ${s.roPill} ${draft.enabled ? s.pillGreen : s.pillGray}`}>
                <i className={s.pillDot} />
                {draft.enabled ? 'On' : 'Off'}
            </span>
        );
    }

    return (
        <div className={cls}>
            {firstCell}

            <div>
                <div className={s.limitTitle}>Weekly limit</div>
                {mode === 'notListed' && <div className={s.limitHelper}>{NOT_LISTED_HINT}</div>}
            </div>

            {draft.enabled && readOnly && (
                <div className={s.roValue}>{draft.limitCount} new patients a week</div>
            )}

            {draft.enabled && !readOnly && (
                <div className={s.num}>
                    {/* 不能给 min / max：rc-input-number 只在值落在范围内时才触发
                        onChange（InputNumber.js:262 的 isRangeValidate 闸门）。
                        给了 min={1} 就意味着输入 0 时 onChange 不触发、state 不变，
                        「The limit has to be at least 1…」这句校验永远出不来；
                        给了 max 更糟——输入 1000 时框里显示 1000 但 state 还是旧值，
                        点 Save 会静默存旧数字。
                        原型的做法是 <input type="text" maxlength="3">：
                        上界靠只能输 3 位挡住，下界交给 validateDraft 出文案。 */}
                    <InputNumber
                        ref={capRef}
                        className={s.numInput}
                        maxLength={3}
                        precision={0}
                        controls={false}
                        disabled={disabled}
                        value={draft.limitCount}
                        onChange={(v) =>
                            onChange({ enabled: draft.enabled, limitCount: v as number | null })
                        }
                    />
                    <span className={s.numUnit}>
                        new patients a week{perProvider ? ', per provider' : ''}
                    </span>
                </div>
            )}

            {draft.enabled && <div className={s.usage}>{showUsage ? usage : null}</div>}

            {errMsg && <div className={s.errMsg}>{errMsg}</div>}
        </div>
    );
};

export default LimitRow;
